import { toHex, mix, getLuminance, getContrast } from 'color2k';

// Perceived brightness (YIQ, 0–255). Pure JS on purpose (no color2k) so the SDK
// bundle can import it without pulling color2k in — the SDK only ships the
// tree-shaken pure helpers from this module.
export const perceivedBrightness = color => {
  const hex = (color || '').replace('#', '');
  if (hex.length < 6) return 255;
  const c_r = parseInt(hex.substr(0, 2), 16);
  const c_g = parseInt(hex.substr(2, 2), 16);
  const c_b = parseInt(hex.substr(4, 2), 16);
  return (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
};

export const isWidgetColorLighter = color => perceivedBrightness(color) > 225;

// Bubble/launcher glyph ink, pure-JS (SDK-safe): dark on a light launcher (e.g.
// a bright lime), white on a dark one — the YIQ 150 midpoint mirrors the widget
// app's WCAG getReadableInk closely enough for the small launcher mark.
export const getBubbleInk = color =>
  perceivedBrightness(color) > 150 ? '#14201c' : '#ffffff';

// Subtle vertical gradient built from a single brand/widget colour, used for
// filled CTAs (send button, submit buttons, card links) so they share one look.
// Stops are pre-computed with color2k so it works without CSS color-mix().
export const getWidgetGradient = color => {
  const c = color || '#0D9488';
  try {
    return `linear-gradient(140deg, ${mix(c, '#ffffff', 0.14)}, ${mix(
      c,
      '#000000',
      0.12
    )})`;
  } catch (e) {
    return c;
  }
};

// Ink (text/icon colour) that stays legible ON a brand-coloured surface.
// WCAG contrast picks white vs a near-black, so a light widget_color (e.g. a
// bright lime) correctly flips the ink to DARK instead of an unreadable white.
// This is the "Kiểu 2" header rule. getContrastingTextColor from @chatlive/utils
// used a YIQ threshold that returned white for mid-bright colours (the washed-out
// header), so the widget uses this WCAG-based check instead.
export const getReadableInk = (color, dark = '#14201c') => {
  try {
    return getContrast(color, '#ffffff') >= getContrast(color, dark)
      ? '#ffffff'
      : dark;
  } catch (e) {
    return '#ffffff';
  }
};

// True when the ink on this colour should be DARK — i.e. the colour is light.
// Lets components pick a dark vs light overlay (e.g. the header status strip).
export const isInkDark = color => getReadableInk(color) !== '#ffffff';

// A brand colour darkened just enough to READ as text/icon/border on a light
// (white) surface. A bright widget_color is illegible as green-on-white, so
// links, quick-reply chips and inline accents darken it toward ~4.5:1. A colour
// already dark enough is returned unchanged.
export const getReadableAccent = (color, target = 4.5) => {
  try {
    let out = color;
    for (let i = 0; i < 24 && getContrast(out, '#ffffff') < target; i += 1) {
      out = mix(out, '#000000', 0.06);
    }
    return toHex(out);
  } catch (e) {
    return color;
  }
};

export const adjustColorForContrast = (color, backgroundColor) => {
  const targetRatio = 3.1;
  const MAX_ITERATIONS = 20;
  let adjustedColor = color;

  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration += 1) {
    const currentRatio = getContrast(adjustedColor, backgroundColor);
    if (currentRatio >= targetRatio) {
      break;
    }
    const adjustmentDirection =
      getLuminance(adjustedColor) < 0.5 ? '#fff' : '#151718';
    adjustedColor = mix(adjustedColor, adjustmentDirection, 0.05);
  }

  return toHex(adjustedColor);
};
